/**
 * Page sharing & permissions (Notion-like), public links for pages and files, and the workspace's sharing settings.
 *
 * Access to a page (core `logic/sharing#resolvePageAccess`, the same function on the server and the clients), from
 * the page up to the root of its subtree:
 *   - Private section (`visibility: "private"`): the owner (root creator) has full access; others only through an
 *     explicit share (nearest up the tree). Owners/admins get nothing here unless invited.
 *   - Workspace section, full members: owners/admins → full; else the nearest explicit share (own beats inherited);
 *     else full on subtrees they created; else the nearest `GeneralAccess` ("none" ⇒ no access); else "edit".
 *   - Restricted members (guests, agents): only subtrees in `AccessPolicy.pageIds`, at the nearest share (default
 *     edit). General access never applies to them. Sharing a page with a restricted member adds it to their pageIds.
 *   - "comment" behaves like "view" until comments exist (the UI shows it with a note).
 * Who may change sharing: full access. Edit access may invite people at edit or lower when
 * `SharingSettings.editorsCanShare` (default yes). Guests never share beyond their own scope. The last person with
 * full access on a private page can't be removed or downgraded.
 *
 * Public links (`/s/<token>`, unguessable, revocable): a read-only render of the page body (and its subpages when
 * `includeSubpages`), no comments, no member data; `noindex` unless `allowIndexing`. Gated by
 * `SharingSettings.publicPages` (admins only / members / off = kill switch: every link stops working at once).
 * File links (`publicFiles`, default admins only) serve the original as an attachment with `noindex`.
 */
import { z } from "zod";
import { Invite } from "./api.ts";
import { BlockProps, BlockType, GeneralAccess, PageAccess, PageKind, PageVisibility } from "./docs.ts";
import { Id, Member, Organization, Timestamp } from "./entities.ts";

// ---------- Settings ----------

/** Who may create public links: every full member, admins/owners only, or nobody ("off" also disables existing links). */
export const PublishPolicy = z.enum(["members", "admins", "off"]);
export type PublishPolicy = z.infer<typeof PublishPolicy>;

export const SharingSettings = z.object({
  /** Publish pages to the web. Default "members". */
  publicPages: PublishPolicy,
  /** Public file links. Default "admins". */
  publicFiles: PublishPolicy,
  /** People with edit access may invite others at edit or lower. Default true. */
  editorsCanShare: z.boolean(),
});
export type SharingSettings = z.infer<typeof SharingSettings>;

/** Admins and owners. Absent fields stay as they are. */
export const UpdateSharingSettingsRequest = SharingSettings.partial();
export type UpdateSharingSettingsRequest = z.infer<typeof UpdateSharingSettingsRequest>;
export const SharingSettingsResponse = z.object({
  settings: SharingSettings,
  /**
   * Owners and admins only: Files items whose general access is "Anyone with the link" and that aren't in the trash
   * (Settings → Sharing → Public file links: "N public links are active"). Counted whether or not the policy is "off".
   */
  publicFileLinks: z.number().int().nonnegative().optional(),
});
export type SharingSettingsResponse = z.infer<typeof SharingSettingsResponse>;

// ---------- Page sharing ----------

export const PageRef = z.object({ id: Id, title: z.string(), icon: z.string().optional() });
export type PageRef = z.infer<typeof PageRef>;

/**
 * Why someone has access: "owner"/"admin" (workspace role, always full, not removable), "creator" (created the page or
 * a page above it), "share" (invited on this page), "inherited" (invited on a page above: `inheritedFrom`).
 */
export const PermissionSource = z.enum(["owner", "admin", "creator", "share", "inherited"]);
export type PermissionSource = z.infer<typeof PermissionSource>;

/** One person in "People with access" (their effective level on this page). */
export const PagePermission = z.object({
  userId: Id,
  access: PageAccess,
  source: PermissionSource,
  inheritedFrom: PageRef.optional(),
  /** Restricted member (guest or agent): scoped to the pages shared with them. */
  restricted: z.boolean().optional(),
});
export type PagePermission = z.infer<typeof PagePermission>;

/** A page's public link (`url` = https://<app>/s/<token>). */
export const PublicLink = z.object({
  token: z.string(),
  url: z.string(),
  createdBy: Id,
  createdAt: Timestamp,
  /** The link stops working after this time. */
  expiresAt: Timestamp.optional(),
  /** Search engines may index it (otherwise `noindex`). */
  allowIndexing: z.boolean(),
  /** Visitors may duplicate it as a template (into a workspace of theirs). */
  allowDuplicate: z.boolean(),
  /** Subpages are published too (read-only, same link). */
  includeSubpages: z.boolean(),
});
export type PublicLink = z.infer<typeof PublicLink>;

/** The Share dialog of one page (GET …/sharing; every sharing mutation returns it again). */
export const PageSharing = z.object({
  pageId: Id,
  visibility: PageVisibility,
  /** Effective general access ("none" = only people invited; private sections are always "none"). */
  general: GeneralAccess,
  /** Where `general` comes from: this page, a page above (`generalFrom`), or the workspace default. */
  generalSource: z.enum(["page", "inherited", "default"]),
  generalFrom: PageRef.optional(),
  /** Everyone with access, owners/admins included (sorted: owners/admins, creator, then by name on the client). */
  people: z.array(PagePermission),
  /** Present while the page is published (to people who can see the page). */
  link: PublicLink.optional(),
  locked: z.boolean(),
  myAccess: PageAccess,
  /** Workspace rules the dialog needs (who may publish, whether editors may invite). */
  settings: SharingSettings,
});
export type PageSharing = z.infer<typeof PageSharing>;
export const PageSharingResponse = z.object({ sharing: PageSharing });
export type PageSharingResponse = z.infer<typeof PageSharingResponse>;

/** PUT …/permissions/:userId: invite a member (or change their level) on this page and its subpages. */
export const SetPagePermissionRequest = z.object({ access: PageAccess });
export type SetPagePermissionRequest = z.infer<typeof SetPagePermissionRequest>;

/** PUT …/general-access: what everyone at the workspace gets; null ⇒ inherit from the page above again. */
export const SetGeneralAccessRequest = z.object({ general: GeneralAccess.nullable() });
export type SetGeneralAccessRequest = z.infer<typeof SetGeneralAccessRequest>;

/**
 * POST …/guests: "Invite as guest" from the Share dialog: a restricted guest scoped to this page's subtree
 * (`AccessPolicy.pageIds = [pageId]`, sponsor = me, no channels), at `access`. Also returns their sign-in link.
 */
export const InvitePageGuestRequest = z.object({
  email: z.string().email(),
  name: z.string().max(80).optional(),
  access: PageAccess,
  /** Default: from the email's domain. */
  organization: Organization.optional(),
  expiresAt: Timestamp.optional(),
});
export type InvitePageGuestRequest = z.infer<typeof InvitePageGuestRequest>;
export const InvitePageGuestResponse = z.object({ member: Member, invite: Invite.optional(), sharing: PageSharing });
export type InvitePageGuestResponse = z.infer<typeof InvitePageGuestResponse>;

/** PUT …/public-link: publish (or change the options of) the page's public link. Absent options keep their value. */
export const PublishPageRequest = z.object({
  allowIndexing: z.boolean().optional(),
  allowDuplicate: z.boolean().optional(),
  includeSubpages: z.boolean().optional(),
  /** null ⇒ never expires. */
  expiresAt: Timestamp.nullable().optional(),
});
export type PublishPageRequest = z.infer<typeof PublishPageRequest>;

// ---------- The public page (/s/:token, no auth) ----------

export const PublicPage = z.object({ id: Id, title: z.string(), icon: z.string().optional(), kind: PageKind });
export type PublicPage = z.infer<typeof PublicPage>;

/** A run of text (core `logic/editor#TextSpan`). Person mentions carry no id; page mentions only to published pages. */
export const PublicSpan = z.object({
  text: z.string(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  strike: z.boolean().optional(),
  code: z.boolean().optional(),
  underline: z.boolean().optional(),
  link: z.string().optional(),
  mention: z.object({ kind: z.enum(["user", "page", "date"]), id: Id.optional(), date: z.string().optional() }).optional(),
});
export type PublicSpan = z.infer<typeof PublicSpan>;

/** A block (core `logic/editor#EditorBlock`): files, images and databases lose their workspace references. */
export const PublicBlock = z.object({
  id: z.string(),
  type: BlockType,
  props: BlockProps,
  text: z.string(),
  spans: z.array(PublicSpan),
  parentId: z.string().optional(),
  children: z.array(z.string()),
});
export type PublicBlock = z.infer<typeof PublicBlock>;

export const PublicPageResponse = z.object({
  page: PublicPage,
  /** The current body only (no history), as an EditorSnapshot. */
  body: z.object({ blocks: z.record(z.string(), PublicBlock), roots: z.array(z.string()) }),
  /** Child pages that open under the same link (includeSubpages). */
  subpages: z.array(PublicPage),
  /** From the published page down to this page's parent (when viewing a subpage). */
  breadcrumbs: z.array(PublicPage),
  allowIndexing: z.boolean(),
  allowDuplicate: z.boolean(),
  updatedAt: Timestamp,
});
export type PublicPageResponse = z.infer<typeof PublicPageResponse>;

// ---------- Files ----------

/** A file's public link (`url` = API URL serving the original as an attachment, `noindex`). */
export const FileLink = z.object({
  token: z.string(),
  url: z.string(),
  createdBy: Id,
  createdAt: Timestamp,
  expiresAt: Timestamp.optional(),
});
export type FileLink = z.infer<typeof FileLink>;

/** The file's Share dialog: its public link and what I may do. */
export const FileSharing = z.object({
  fileId: Id,
  link: FileLink.optional(),
  /** May post it to another conversation (I can download it). */
  canShare: z.boolean(),
  /** May create/revoke a public link (I can download it and `publicFiles` allows me). */
  canManageLink: z.boolean(),
  settings: SharingSettings,
});
export type FileSharing = z.infer<typeof FileSharing>;
export const FileSharingResponse = z.object({ sharing: FileSharing });
export type FileSharingResponse = z.infer<typeof FileSharingResponse>;

/** PUT …/files/:fileId/public-link. null/absent expiresAt ⇒ never expires. */
export const CreateFileLinkRequest = z.object({ expiresAt: Timestamp.nullable().optional() });
export type CreateFileLinkRequest = z.infer<typeof CreateFileLinkRequest>;

/** POST …/files/:fileId/share: posts the file (a new copy of the reference) to a conversation I'm in. */
export const ShareFileRequest = z.object({
  channelId: Id,
  text: z.string().max(40_000).optional(),
  threadRootId: Id.optional(),
  clientId: z.string().max(64).optional(),
});
export type ShareFileRequest = z.infer<typeof ShareFileRequest>;
